import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import DocumentsUploadHistoryModal from './DocumentUploadHistoryModal';
import DocumentsUploadCardComponent from './DocumentsUploadCardComponent';
import { CHANGE_DOCUMENT_ATTACHMENT_STATE_MUTATION, UPDATE_DOCUMENT_DATA_MUTATION } from '../mutations';
import { DOCUMENT_ATTACHMENT_UPDATED } from '../subscriptions';
import { buildDMSDownloadUrl } from '../actionCreators';
import { Box, Card, Grid } from '@mui/material';
import { Prompt } from 'react-router-dom';
import * as editableDocument from 'utils/model/editableDocument';
import flow from 'lodash.flow';
import { hasFile, hasLengthLessThan, isValidRocketUIDateFormat } from 'utils/simple-validations';
import { maximumCommentLength } from '../../../../../constants/numericLimits';
import { showError } from 'actions/actionCreators/snackbarActions';
import { loadPropDetails } from 'features/offer/edit/client/properties/actionCreators';
import { useFileUpload } from '../hooks/fileHandling';
import DocumentsUploadTransitionsModal from './DocumentsUploadTransitionsModal';
import { isMandatory, foreach } from 'utils/simple-validations';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import DocumentsUploadEditComponent from './DocumentsUploadEditComponent';
import DocumentUploadPreviewModal from './DocumentUploadPreviewModal';
import { ChecklistCardContent } from './DocumentsStyles';
import { when } from 'utils/fn-combinators';
import MessageBoxModal from './MessageBoxModal';
import { getDynamicFieldControlType } from 'utils/functions';
import { ControlType } from '@totalsoft/rocket-ui';
import { format } from 'date-fns';

const getItemValidationRules = (activeProduct, file) => ({
  valabilityDate: [hasFile(file), isValidRocketUIDateFormat()],
  comments: [hasLengthLessThan(maximumCommentLength)],
  attachmentProperties: foreach(property => ({
    propertyValue: [
      activeProduct && property.propertyMandatory && isMandatory(),
      when(getDynamicFieldControlType(property.propertyTypeCode) === ControlType.Date, isValidRocketUIDateFormat())
    ]
  }))
});

const DocumentsUploadCardContainer = ({
  documentId,
  siteId,
  changeLocalDocuments,
  document,
  uploadDocumentWrite,
  readOnlyFull,
  onDelete,
  onDeleteFiles,
  onSelectDocument,
  actionsStates,
  setActionsStates,
  filesUploadInProgress,
  setFilesUploadInProgress
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [localDocument, setLocalDocument] = useState({});
  const [showDocumentUploadHistoryModal, setShowDocumentUploadHistoryModal] = useState(false);
  const [showDocumentUploadTransitionsModal, setShowDocumentUploadTransitionsModal] = useState(false);

  const [saving, setSaving] = useState({ attachmentId: null, inProgress: false });
  const [newTransitionState, setNewTransitionState] = useState(null);
  const [showModalMessageBox, setShowModalMessageBox] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showModalPreview, setShowModalPreview] = useState(false);
  const [shouldUploadFile, setShouldUploadFile] = useState(false);
  const [fileData, setFileData] = useState({ fileId: null, file: null });

  const onOpenModalMessageBox = useCallback(() => {
    setShowModalMessageBox(true);
  }, []);

  const onCloseModalMessageBox = useCallback(() => {
    setShowModalMessageBox(false);
  }, []);

  const [changeDocumentAttachmentState] = useMutation(CHANGE_DOCUMENT_ATTACHMENT_STATE_MUTATION, {
    onError: error => {
      showError(error.message);
      setSaving({ attachmentId: null, inProgress: false });
    },
    onCompleted: r => {
      const { correlationId } = r.changeDocumentAttachmentState;
      storeKey({ correlationId });
    }
  });

  const onConfirmationMessageBox = useCallback(() => {
    onCloseModalMessageBox();

    if (!newTransitionState) return;
    const _flow = flow(editableDocument.update('saving', true, null))(localDocument);
    setLocalDocument(_flow);

    changeDocumentAttachmentState({
      variables: {
        input: {
          documentId,
          siteId,
          documentAttachmentId: localDocument.document.attachmentId,
          flowId: newTransitionState.flowId,
          transitionId: newTransitionState.transitionId,
          stateId: newTransitionState.finalStateId
        }
      }
    });
    setNewTransitionState(null);
  }, [changeDocumentAttachmentState, documentId, localDocument, newTransitionState, onCloseModalMessageBox, siteId]);

  const onOpenModalEdit = useCallback(() => {
    setShowModalEdit(true);
  }, []);

  const onCloseModalEdit = useCallback(() => {
    setLocalDocument(editableDocument.create(document));
    setShowModalEdit(false);
    setShouldUploadFile(false);
    setFileData({ fileId: null, file: null });
  }, [document]);

  const onOpenModalPreview = useCallback(
    event => {
      if (event.shiftKey) {
        window.open(`${window.location.href}/${document.fileId}`, '_blank', 'width=800,height=800', 'noopener,noreferrer');
        return;
      }
      setShowModalPreview(true);
    },
    [document.fileId]
  );

  const onCloseModalPreview = useCallback(() => {
    setShowModalPreview(false);
  }, []);

  useEffect(() => {
    if (document) {
      setLocalDocument(editableDocument.create(document));
    }
  }, [document]);

  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      filesUploadInProgress.forEach(state => {
        state.inProgress = false;
      });
      setFilesUploadInProgress([...filesUploadInProgress]);
      setSaving({ attachmentId: null, inProgress: false });
    }
  });

  const [uploadDocument] = useFileUpload({
    onError: error => {
      if (error) dispatch(showError(error.message));
      filesUploadInProgress.forEach(state => {
        state.inProgress = false;
      });
      setFilesUploadInProgress([...filesUploadInProgress]);
    },
    onCompleted: r => {
      const { correlationId } = r.uploadDocument;
      storeKey({ correlationId });
    }
  });

  const [updateDocumentData] = useMutation(UPDATE_DOCUMENT_DATA_MUTATION, {
    onError: error => {
      showError(error.message);
      setSaving({ attachmentId: null, inProgress: false });
    },
    onCompleted: r => {
      const { correlationId } = r.updateDocumentData;
      storeKey({ correlationId });
    }
  });

  const uploadFile = useCallback(
    (attachmentId, attachmentTypeId, contextId, fileId, file, shouldUpdateDocumentData = false) => {
      async function uploadFileAsync() {
        const doc = editableDocument.validate(
          getItemValidationRules(localDocument.document.validatePropertiesOnUpload, document.fileId || fileData.file),
          false,
          localDocument
        );
        setLocalDocument(doc);

        if (!doc.validationInfo.isValid) {
          onOpenModalEdit();
          setShouldUploadFile(true);
          setFileData({ fileId, file });
          return;
        }

        const formData = { attachmentId, attachmentTypeId, contextId, fileId };
        const existingAttachment = filesUploadInProgress.find(state => state.attachmentId === attachmentId);

        if (!existingAttachment) {
          const updatedFiles = [...filesUploadInProgress, { attachmentId, inProgress: true }];
          setFilesUploadInProgress(updatedFiles);
        } else {
          const updatedFiles = filesUploadInProgress.map(state =>
            state.attachmentId === attachmentId ? { ...state, inProgress: true } : state
          );
          setFilesUploadInProgress(updatedFiles);
        }
        shouldUpdateDocumentData
          ? uploadDocument(documentId, siteId, file, formData, doc.document)
          : uploadDocument(documentId, siteId, file, formData);
      }
      uploadFileAsync();
    },
    [
      localDocument,
      document.fileId,
      fileData.file,
      filesUploadInProgress,
      setFilesUploadInProgress,
      uploadDocument,
      documentId,
      siteId,
      onOpenModalEdit
    ]
  );

  const handleFileSelected = useCallback(
    files => {
      if (!files || files.length === 0) return;
      const document = localDocument.document;
      uploadFile(document.attachmentId, document.attachmentTypeId, document.contextId, document.fileId, files[0]);
    },
    [localDocument.document, uploadFile]
  );

  const onDocumentUploadHistoryClick = useCallback(() => {
    setShowDocumentUploadHistoryModal(true);
  }, []);

  const onCloseDocumentUploadHistoryModal = useCallback(() => {
    setShowDocumentUploadHistoryModal(false);
  }, []);

  const onDocumentsUploadTransitionsClick = useCallback(() => {
    setShowDocumentUploadTransitionsModal(true);
  }, []);

  const onCloseDocumentUploadTransitionsModal = useCallback(() => {
    setShowDocumentUploadTransitionsModal(false);
  }, []);

  const handlePropertyItemChange = useCallback(
    (prop, value) => {
      editableDocument.validate(
        getItemValidationRules(localDocument.document.validatePropertiesOnUpload, document.fileId || fileData.file),
        null
      );

      const _flow = flow(
        editableDocument.update(prop, value, null),
        editableDocument.validate(
          getItemValidationRules(localDocument.document.validatePropertiesOnUpload, document.fileId || fileData.file),
          null
        )
      );
      setLocalDocument(_flow);
    },
    [document.fileId, fileData.file, localDocument]
  );

  const onSave = useCallback(() => {
    const onSaveAsync = async () => {
      const doc = editableDocument.validate(
        getItemValidationRules(localDocument.document.validatePropertiesOnUpload, document.fileId || fileData.file),
        false,
        localDocument
      );
      setLocalDocument(doc);

      if (!doc.validationInfo.isValid) {
        onOpenModalEdit();
        return;
      }

      setSaving({ attachmentId: doc.document.attachmentId, inProgress: true });

      try {
        localDocument.document.attachmentProperties.forEach(prop => {
          if (getDynamicFieldControlType(prop.propertyTypeCode) !== ControlType.Date || !prop.propertyValue) return;
          prop.propertyValue = format(new Date(prop.propertyValue), 'yyyy-MM-dd').toString();
        });

        if (fileData.file && shouldUploadFile) {
          uploadFile(
            doc.document.attachmentId,
            doc.document.attachmentTypeId,
            doc.document.contextId,
            fileData.fileId,
            fileData.file,
            true
          );
          setShouldUploadFile(false);
          setFileData({ fileId: null, file: null });
        } else {
          updateDocumentData({
            variables: {
              input: { documentId, siteId, documentAttachment: localDocument.document }
            }
          });
        }

        const _flow = flow(editableDocument.resetDirtyInfo, editableDocument.resetValidationInfo);
        setLocalDocument(_flow);
        changeLocalDocuments(doc, localDocument.document.attachmentId);
      } catch (error) {
        showError(error.message);
      }
    };
    onSaveAsync();
    onCloseModalEdit();
  }, [
    onCloseModalEdit,
    localDocument,
    document.fileId,
    fileData.file,
    fileData.fileId,
    shouldUploadFile,
    onOpenModalEdit,
    updateDocumentData,
    documentId,
    siteId,
    changeLocalDocuments,
    uploadFile
  ]);

  const downloadFile = useCallback(fileId => {
    async function downloadFileAsync() {
      try {
        let link = buildDMSDownloadUrl(fileId)();
        window.location = link;
      } catch (error) {
        showError(error.message);
      }
    }
    downloadFileAsync();
  }, []);

  const downloadClick = useCallback(() => {
    downloadFile(document.fileId);
  }, [document.fileId, downloadFile]);

  const loadPropertyDetails = useCallback(
    propId => {
      return dispatch(loadPropDetails(propId)).then(data => {
        return data;
      });
    },
    [dispatch]
  );

  const handleChangeStateTransitionClick = useCallback(
    newState => {
      const onSaveAsync = async newState => {
        const doc = editableDocument.validate(
          getItemValidationRules(newState.finalStateActiveProduct, document.fileId || fileData.file),
          false,
          localDocument
        );
        setLocalDocument(doc);

        if (!localDocument.document.isUploaded && localDocument.document.isDeleted && !newState.isInitialOrPostUploadTransition) {
          showError(
            t('OfferEdit.Documents.DocumentAttachmentApprovalFlow.ErrorMessage', {
              data: localDocument.document.attachmentTypeName
            })
          );
          return;
        }

        if (!doc.validationInfo.isValid) {
          onOpenModalEdit();
          return;
        }
        setNewTransitionState(newState);
        onOpenModalMessageBox();
      };
      onSaveAsync(newState);
    },
    [document.fileId, fileData.file, localDocument, onOpenModalMessageBox, t, onOpenModalEdit]
  );

  useGlobalSubscription(DOCUMENT_ATTACHMENT_UPDATED, {
    variables: {
      documentId,
      siteId,
      attachmentId: document.attachmentId
    },
    onSubscriptionData: _ => {
      setSaving({ attachmentId: null, inProgress: false });
      setActionsStates(prev => ({ ...prev, readOnlyAll: false }));
    }
  });

  const getMessage = useCallback(() => t('LeavingWithoutSaving'), [t]);

  return (
    <>
      {localDocument.document && (
        <>
          <Prompt when={localDocument.dirtyInfo.isDirty} message={getMessage} />
          <Card>
            <Grid container>
              <Grid item xs='auto'>
                <Box sx={{ width: 8, height: 1, backgroundColor: localDocument.document.stateColor }} />
              </Grid>
              <Grid item xs zeroMinWidth>
                <ChecklistCardContent>
                  <DocumentsUploadCardComponent
                    index={localDocument.document.uniqueId}
                    document={localDocument.document}
                    uploadClick={handleFileSelected}
                    downloadClick={downloadClick}
                    disabled={
                      (localDocument.document.attachmentId === saving.attachmentId && saving.inProgress) ||
                      actionsStates.readOnlyAll ||
                      (localDocument.document.attachmentId === actionsStates.item.attachmentId && actionsStates.item.deleting) ||
                      localDocument.document.saving
                    }
                    onDelete={onDelete}
                    onDeleteFiles={onDeleteFiles}
                    validationInfo={localDocument.validationInfo || {}}
                    dirtyInfo={localDocument.dirtyInfo || {}}
                    onDocumentUploadHistoryClick={onDocumentUploadHistoryClick}
                    onChangeStateTransitionClick={handleChangeStateTransitionClick}
                    onDocumentsUploadTransitionsClick={onDocumentsUploadTransitionsClick}
                    readOnlyFull={readOnlyFull}
                    filesUploadInProgress={filesUploadInProgress}
                    onEditClick={onOpenModalEdit}
                    onSelectDocument={onSelectDocument}
                    onPreviewClick={onOpenModalPreview}
                  />
                </ChecklistCardContent>
              </Grid>
            </Grid>
          </Card>
        </>
      )}

      {showModalEdit && (
        <DocumentsUploadEditComponent
          index={localDocument.document.uniqueId}
          open={showModalEdit}
          onCloseModal={onCloseModalEdit}
          onSave={onSave}
          onPropertyChange={handlePropertyItemChange}
          document={localDocument.document}
          loadPropertyDetails={loadPropertyDetails}
          saving={saving}
          readOnlyFull={readOnlyFull}
          uploadDocumentWrite={uploadDocumentWrite}
          validationInfo={localDocument.validationInfo || {}}
          dirtyInfo={localDocument.dirtyInfo || {}}
        />
      )}

      {showModalPreview && (
        <DocumentUploadPreviewModal open={showModalPreview} fileId={document.fileId} onCloseModal={onCloseModalPreview} />
      )}

      {showDocumentUploadHistoryModal && (
        <DocumentsUploadHistoryModal
          onCloseDocumentUploadHistoryModal={onCloseDocumentUploadHistoryModal}
          documentId={documentId}
          siteId={siteId}
          fileId={document.fileId}
        />
      )}

      {showDocumentUploadTransitionsModal && (
        <DocumentsUploadTransitionsModal
          onCloseDocumentUploadTransitionsModal={onCloseDocumentUploadTransitionsModal}
          documentAttachmentId={document.attachmentId}
        />
      )}

      {showModalMessageBox && (
        <MessageBoxModal
          open={showModalMessageBox}
          onConfirmation={onConfirmationMessageBox}
          onCloseModal={onCloseModalMessageBox}
          title={t('OfferEdit.Documents.DocumentAttachmentApprovalFlow.Title')}
          text={t('OfferEdit.Documents.DocumentAttachmentApprovalFlow.Content')}
        />
      )}
    </>
  );
};

DocumentsUploadCardContainer.propTypes = {
  document: PropTypes.object,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  uploadDocumentWrite: PropTypes.bool.isRequired,
  readOnlyFull: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDeleteFiles: PropTypes.func.isRequired,
  changeLocalDocuments: PropTypes.func.isRequired,
  onSelectDocument: PropTypes.func.isRequired,
  actionsStates: PropTypes.object.isRequired,
  setActionsStates: PropTypes.func.isRequired,
  filesUploadInProgress: PropTypes.array.isRequired,
  setFilesUploadInProgress: PropTypes.func.isRequired
};

export default DocumentsUploadCardContainer;
